<script lang="ts" setup>
import { ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useI18n } from 'vue-i18n';
import ResponsiveTurnstileWidget from '@/components/ResponsiveTurnstileWidget.vue';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;

  (e: 'token-received', token: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const turnstileToken = ref('');

const handleClose = () => {
  emit('close');
};

const handleTokenSuccess = (token: string) => {
  turnstileToken.value = token;
  emit('token-received', token);
  handleClose();
};

const handleTokenError = (error: string) => {
  turnstileToken.value = '';
  console.error('Turnstile error:', error);
};

const handleTokenExpired = () => {
  turnstileToken.value = '';
};

const { t } = useI18n();

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      turnstileToken.value = '';
    }
  }
);
</script>

<template>
  <TransitionRoot :show="isOpen" as="template">
    <Dialog class="relative z-10" @close="handleClose">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div>
                <div class="my-3 text-center sm:my-5">
                  <DialogTitle as="h3" class="text-base font-semibold text-gray-900">
                    {{ t('components.turnstile-modal.title') }}
                  </DialogTitle>
                  <div class="mt-4">
                    <p class="text-xs tracking-tight text-gray-500">
                      {{ t('components.turnstile-modal.description') }}
                    </p>
                  </div>
                  <div class="mt-6">
                    <ResponsiveTurnstileWidget @error="handleTokenError" @expired="handleTokenExpired" @success="handleTokenSuccess" />
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
